import {useEffect, useRef, useState} from "react";

/**
 * Custom hook to analyze the output volume of an audio element using RMS
 * and return the average volume.
 */
export function useAnalyzeAudioOutputVolume(
    audioEl: HTMLAudioElement | null,
    isPlaying: boolean,
): number {
    const [outputVolumeAvg, setOutputVolumeAvg] = useState(0);
    const audioCtxRef = useRef<AudioContext | null>(null);
    const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);

    // Thorough cleanup on unmount
    useEffect(() => {
        return () => {
            try {
                analyserRef.current?.disconnect();
                sourceRef.current?.disconnect();
                audioCtxRef.current?.close();
            } catch (e) {
                // Ignore cleanup errors
            }
        };
    }, []);

    useEffect(() => {
        if (!audioEl || !isPlaying) {
            setOutputVolumeAvg(0);
            return;
        }
        // Create AudioContext and MediaElementAudioSourceNode if not already created
        if (!audioCtxRef.current) {
            try {
                audioCtxRef.current = new AudioContext();
            } catch (error) {
                // If AudioContext creation fails, exit early
                setOutputVolumeAvg(0);
                return;
            }
        }

        // Check if we need to create a new source node
        // This could happen if audioEl reference changed
        if (!sourceRef.current || sourceRef.current.mediaElement !== audioEl) {
            // Clean up old source if it exists
            if (sourceRef.current) {
                try {
                    sourceRef.current.disconnect();
                } catch (e) {
                    // Ignore disconnect errors
                }
            }

            // Create new source for the current audio element
            sourceRef.current =
                audioCtxRef.current.createMediaElementSource(audioEl);
            sourceRef.current.connect(audioCtxRef.current.destination);
        }

        if (
            !isPlaying ||
            !audioCtxRef.current ||
            !sourceRef.current ||
            audioCtxRef.current.state === "suspended"
        ) {
            setOutputVolumeAvg(0);
            return;
        }

        // Reuse existing analyser or create new one if needed
        if (!analyserRef.current) {
            const analyser = audioCtxRef.current.createAnalyser();
            analyserRef.current = analyser;
            analyser.fftSize = 2048;
            sourceRef.current.connect(analyser);
        }

        const dataArray = new Float32Array(analyserRef.current!.fftSize);
        let running = true;
        function checkVolume() {
            if (!analyserRef.current) {
                return;
            }
            analyserRef.current.getFloatTimeDomainData(dataArray);
            // Calculate the RMS of the current samples. Common technique for measuring audio volume.
            const rms = Math.sqrt(
                dataArray.reduce((sum, v) => sum + v * v, 0) / dataArray.length,
            );
            setOutputVolumeAvg(rms);
            // We use requestAnimationFrame for real-time audio analysis because it provides the necessary
            // timing precision for audio visualization.
            if (running) {
                // eslint-disable-next-line no-restricted-syntax -- no hooks-based requestAnimationFrame
                requestAnimationFrame(checkVolume);
            }
        }
        checkVolume();
        return () => {
            running = false;
            analyserRef.current?.disconnect();
            analyserRef.current = null;
        };
    }, [isPlaying, audioEl]);

    return outputVolumeAvg;
}
